import { REG_KEYS, regDateOf } from './regdate';
import { genDocs, fileUrl, fetchFileBlob, uploadPreview, findAttach, GenDocResult } from './api';
import { GENDOCS, getVers, autoV1, genFileName } from './gendocs';
import type { Project, DocVer, DocVerMap, Master } from './types';
import { projectDir, saveToProject, savedMsg, SaveCancelled, chooseFolderOnce } from './projectFolder';

export const ALLKEYS: string[] = Object.keys(GENDOCS);

/** 생성 결과를 버전 목록에 반영: 기본 자동 v1(placeholder)뿐이면 교체, 아니면 '재생성 vN' 추가 */
function applyResult(dv: DocVerMap, k: string, r: GenDocResult, p: Project): DocVer {
  // 서류에 찍힌 날짜를 같이 적어 둔다 — 등록 서류 날짜 점검(regdate.ts)이 「다른 날짜로 만든 서류」를 찾는다
  const m = p.master as Master | undefined;
  const date = m ? ((REG_KEYS as readonly string[]).includes(k) ? regDateOf(m) : (m.applyDate || '').slice(0, 10)) : '';
  const list = dv[k] && dv[k].length ? dv[k] : [autoV1(p, k)];
  const onlyFake = list.length === 1 && list[0].kind === 'auto' && !list[0].s3doc;
  const ver: DocVer = {
    label: onlyFake ? '자동 생성 v1' : '재생성 v' + (list.length + 1),
    doc: r.doc, pdf: r.pdf, kind: 'auto', s3doc: r.s3doc, s3pdf: r.s3pdf, at: new Date().toISOString(), ...(date ? { date } : {}),
  };
  dv[k] = onlyFake ? [ver] : [...list, ver];
  return ver;
}

/** 키 배열 순차 실생성 (문서당 2~4초) → docver 패치 */
export async function regenDocs(
  p: Project,
  keys: string[],
  onPatch: (patch: Partial<Project>) => void,
  toast: (m: string) => void,
): Promise<DocVerMap> {
  const master = p.master as Master | undefined;
  if (!master) { toast('마스터 정보가 없습니다 — 3단계에서 먼저 입력하세요'); throw new Error('no master'); }
  const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
  toast('문서 생성 시작 · ' + keys.length + '건 (건당 2~4초)');
  let fail = 0;
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    try {
      const { results } = await genDocs(p.id, [k], master, p.regDate, { cos: p.cos || [], bankForm: (p.s5 && p.s5.txt && p.s5.txt.bankguide_form) || null });
      applyResult(dv, k, results[0], p);
      onPatch({ docver: JSON.parse(JSON.stringify(dv)) as DocVerMap });
      toast('생성 ' + (i + 1) + '/' + keys.length + ' · ' + (GENDOCS[k] ? GENDOCS[k].title : k) + (results[0].converted ? '' : ' (⚠ PDF 변환 실패)'));
    } catch (e) {
      fail++;
      toast('생성 실패 · ' + (GENDOCS[k] ? GENDOCS[k].title : k) + ' — ' + (e as Error).message);
    }
  }
  toast(fail ? '완료 · 실패 ' + fail + '건은 다시 시도하세요' : '문서 생성 완료 · Word·PDF가 실제 파일로 저장되었습니다');
  return dv;
}

/** 현재 버전이 실파일이 아니면 1건 생성 후 그 버전을 반환 */
export async function ensureReal(
  p: Project,
  k: string,
  onPatch: (patch: Partial<Project>) => void,
  toast: (m: string) => void,
): Promise<DocVer> {
  const vers = getVers(p, k);
  const cur = vers[vers.length - 1];
  if (cur.s3doc || cur.s3pdf) return cur;
  const dv = await regenDocs(p, [k], onPatch, toast);
  const list = dv[k];
  return list[list.length - 1];
}

export async function openS3(s3key: string, name: string, inline?: boolean): Promise<void> {
  const url = await fileUrl(s3key, name, inline);
  window.open(url, '_blank', 'noopener');
}

// ── 일괄저장(폴더) ──────────────────────────────────────────────
export interface SaveFile { key: string; name: string; signed?: boolean; getBlob?: () => Promise<Blob>; }

/** 단계별 생성문서(키 목록)의 실파일(.docx/.pdf 최신본)을 모은다. 저장본 없는 키는 자동 제외 */
export function collectGenFiles(p: Project, keys: string[]): SaveFile[] {
  const out: SaveFile[] = [];
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const v = getVers(p, k).slice(-1)[0];
    if (v.s3doc) out.push({ key: v.s3doc, name: genFileName(p, k, 'docx') });
    if (v.s3pdf) out.push({ key: v.s3pdf, name: genFileName(p, k, 'pdf'), signed: v.kind === 'seal' });
  }
  return out;
}

function uniqueName(name: string, used: Set<string>): string {
  if (!used.has(name)) { used.add(name); return name; }
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  let n = 2;
  let cand = base + ' (' + n + ')' + ext;
  while (used.has(cand)) { n++; cand = base + ' (' + n + ')' + ext; }
  used.add(cand);
  return cand;
}

function triggerDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

interface RoumDirHandle { getFileHandle(name: string, opts?: { create?: boolean }): Promise<RoumFileHandle>; }
interface RoumFileHandle { createWritable(): Promise<RoumWritable>; }
interface RoumWritable { write(data: Blob): Promise<void>; close(): Promise<void>; }

/** 단계 문서 일괄 다운로드 — 폴더 선택(showDirectoryPicker) 지원 시 고른 폴더에 직접 저장,
 *  미지원/차단 시 브라우저 기본 다운로드 폴더로 개별 저장(폴백). 취소 시 중단.
 *  ※ Chrome은 바탕화면·다운로드 등 시스템 폴더 '자체'는 거부 → 그 안에 하위 폴더를 만들어 선택해야 함 */
export async function saveFilesToFolder(files: SaveFile[], toast: (m: string) => void, pid?: string): Promise<void> {
  const list = files.filter((f) => f.name && (f.key || f.getBlob));
  if (!list.length) { toast('저장할 파일이 없습니다 · 먼저 문서를 생성/업로드하세요'); return; }
  const used = new Set<string>();
  // 1) 폴더 선택 시도 (Chrome·Edge)
  const picker = (window as unknown as { showDirectoryPicker?: (o?: { mode?: string; startIn?: string }) => Promise<RoumDirHandle> }).showDirectoryPicker;
  if (typeof picker === 'function') {
    let dir: RoumDirHandle | null = null;
    try {
      // 조합 저장 폴더가 있으면 그 폴더(처음 한 번만 고름 · 2026-11-01) · 조합 모르면 매번 고르기
      dir = pid ? await projectDir(pid) : await chooseFolderOnce();   // 조합 모르면 안내 창을 거쳐 이번만 고르기
    } catch (e) {
      // 사용자가 취소(AbortError) → 중단. 그 외(차단 등)는 개별 다운로드로 폴백
      if (e instanceof SaveCancelled || (e && (e as { name?: string }).name === 'AbortError')) return;
      dir = null;
    }
    if (dir) {
      toast('폴더에 저장 시작 · ' + list.length + '건');
      let okN = 0; let failN = 0;
      for (let i = 0; i < list.length; i++) {
        try {
          const gb = list[i].getBlob; const blob = gb ? await gb() : await fetchFileBlob(list[i].key, list[i].name);
          const fh = await dir.getFileHandle(pid ? list[i].name : uniqueName(list[i].name, used), { create: true });   // 조합 폴더면 같은 이름 덮어씀(최신본)
          const ws = await fh.createWritable();
          await ws.write(blob);
          await ws.close();
          okN++;
        } catch (e) { failN++; }
      }
      toast('폴더 저장 완료 · ' + okN + '건' + (failN ? ' · 실패 ' + failN + '건' : ''));
      return;
    }
  }
  // 2) 폴백: 브라우저 다운로드 폴더로 개별 저장 (기존 동작)
  toast('전체 다운로드 시작 · ' + list.length + '건 (브라우저 다운로드 폴더)');
  let okN = 0; let failN = 0;
  for (let i = 0; i < list.length; i++) {
    try {
      const gb = list[i].getBlob; const blob = gb ? await gb() : await fetchFileBlob(list[i].key, list[i].name);
      triggerDownload(blob, uniqueName(list[i].name, used));
      okN++;
      await sleep(300);
    } catch (e) { failN++; }
  }
  toast('전체 다운로드 완료 · ' + okN + '건' + (failN ? ' · 실패 ' + failN + '건' : ''));
}

/** S3 키 없는 업로드(자료수집 연동/이름 기준)을 Blob으로 — 일괄저장에서 키 없는 항목 처리용 */
export async function fetchCollectBlob(p: Project, name: string): Promise<Blob> {
  const c = p.collect;
  if (c) {
    const cards = [c.gp, ...(c.lps || [])];
    for (let i = 0; i < cards.length; i++) {
      const cd = cards[i];
      if (!cd || !cd.collectProjectId || !cd.results) continue;
      const r = cd.results.find((x) => x.value === name && x.uploadId);
      if (r && r.uploadId) {
        const pv = await uploadPreview(r.uploadId, true);
        const res = await fetch(pv.url);
        if (!res.ok) throw new Error('연동 파일 다운로드 실패');
        return res.blob();
      }
    }
  }
  const at = await findAttach(p.id, name, true);
  const res = await fetch(at.url);
  if (!res.ok) throw new Error('연동 파일 다운로드 실패');
  return res.blob();
}

/** 자료수집 업로드 파일(이름 기준)을 찾아 다운로드 — RefFile 다운로드 버튼용 */
export async function downloadCollectFile(p: Project, name: string, toast: (m: string) => void): Promise<void> {
  if (!name) { toast('파일이 없습니다 (미제출)'); return; }
  try { toast(savedMsg(await saveToProject(p.id, name, () => fetchCollectBlob(p, name)), name)); }   // 조합 저장 폴더로(2026-11-01)
  catch (e) { toast((e as Error).message); }
}

/** 업로드 첨부 다운로드 — 로컬 S3키 있으면 바로, 없으면 이름으로 S3 검색(키 미저장 옛 업로드 복구) */
export async function downloadUploadFile(p: Project, localKey: string, name: string, toast: (m: string) => void): Promise<void> {
  if (!name) { toast('파일이 없습니다 (미제출)'); return; }
  try { toast(savedMsg(await saveToProject(p.id, name, () => (localKey ? fetchFileBlob(localKey, name) : fetchCollectBlob(p, name))), name)); }   // 조합 저장 폴더로 · 키 없으면 이름으로 찾기(2026-11-01)
  catch (e) { toast((e as Error).message); }
}

/** S3 파일 한 건을 조합 저장 폴더로 — 원본 · 버전 · 사채 · 변경내역 등 「⬇」 단추 공용 (2026-11-01) */
export async function saveS3ToProject(p: Project, s3key: string, name: string, toast: (m: string) => void): Promise<void> {
  try { toast(savedMsg(await saveToProject(p.id, name, () => fetchFileBlob(s3key, name)), name)); }
  catch (e) { toast((e as Error).message); }
}
